<template>
    <div class="form-container" data-cy="view-container">
        <div class="form-section">
            <div
                :class="{
                    'section-header': section_type != 'site_admin' && section_type != 'association',
                    'p-4 pb-0': section_type == 'site_admin' || section_type == 'association',
                }"
            >
                <div class="view-borderline flex flex-wrap w-full mb-2">
                    <div
                        class="header-text w-full lg:w-1/5"
                        :class="{ 'pt-3 pl-2': section_type != 'site_admin' && section_type != 'association' }"
                        data-cy="claim-detail-header"
                    >
                        {{ $t('claim.view.headers.details') }}
                    </div>

                    <div class="sub-header w-full sm:w-2/5 md:w-1/2 lg:w-2/5" data-cy="claim-detail-insured-header">
                        {{ claim.company_code }} : {{ claim.insured }} [{{ claim.trn }}]
                    </div>

                    <div
                        class="sub-header w-full sm:w-3/5 md:w-1/2 lg:text-right lg:w-2/5"
                        data-cy="claim-detail-updated-header"
                    >
                        {{ lastUpdatedAt }}
                    </div>
                </div>
            </div>

            <div class="section-body px-4 w-full flex-col mb-8" data-cy="claim-details">
                <div class="flex w-full px-2 mb-2" data-cy="claim-name-field">
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.claim_no') }}
                    </ui-label>
                    <div class="subText w-1/4 lg:w-2/4">
                        <span v-if="claim.claim_id_old">{{ claim.claim_id }} : {{ claim.claim_id_old }}</span>
                        <span v-else>
                            {{ claim.claim_id }}
                        </span>
                    </div>
                    <div class="subText w-1/4" v-if="claim.cb_status">[{{ claim.cb_status.toUpperCase() }}]</div>
                    <div class="subText w-1/4" v-else>[]</div>
                </div>
                <div class="flex w-full px-2 mb-2" data-cy="claim-loss-field">
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.loss_date') }}
                    </ui-label>
                    <div class="subText w-1/2 lg:w-3/4">
                        {{ claim.date_of_loss }}
                    </div>
                </div>
                <div class="flex w-full px-2 mb-2" data-cy="claim-cause-field">
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.cause_of_incident') }}
                    </ui-label>
                    <div class="subText w-1/4 lg:w-2/4">
                        {{ claim.cause_of_accident }}
                    </div>
                </div>
                <div class="flex w-full px-2 mb-2" data-cy="claim-location-field">
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.location') }}
                    </ui-label>
                    <div class="subText w-1/2 lg:w-3/4">
                        <span v-if="claim.postal_zone">{{ claim.location }} : {{ claim.postal_zone }}</span>
                        <span v-else>{{ claim.location }}</span>
                    </div>
                </div>
                <div class="flex w-full px-2 mb-2" data-cy="claim-retail-field">
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.risk_detail') }}
                    </ui-label>
                    <div class="subText w-1/2 lg:w-3/4">
                        {{ claim.risk_details }}
                    </div>
                </div>
                <div class="flex w-full px-2 mb-2" data-cy="claim-vin-field">
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.vin_no') }}
                    </ui-label>
                    <div class="subText w-1/4 lg:w-2/4">
                        <span>
                            {{ claim.chassis_number }}
                        </span>
                    </div>
                    <div class="subText w-1/4 lg:w-1/4" v-if="claim.is_salvage_parts">
                        {{ $t('claim.view.data.is_salvage_parts') }}
                    </div>
                </div>
                <div class="flex w-full px-2 mb-2" data-cy="claim-driver-field" v-if="claim.is_motor">
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.driver_info') }}
                    </ui-label>
                    <div class="subText w-1/2 lg:w-3/4">
                        {{ claim.driver_info }}
                    </div>
                </div>
                <div class="flex w-full px-2 mb-2" data-cy="claim-license-field" v-if="claim.is_motor">
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.driver_license_info') }}
                    </ui-label>
                    <div class="subText w-1/2 lg:w-3/4">
                        {{ claim.driver_license_info }}
                    </div>
                </div>
                <div class="flex w-full px-2 mb-2" data-cy="claim-license-field" v-if="!claim.is_motor">
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.description_of_cover') }}
                    </ui-label>
                    <div class="subText w-1/2 lg:w-3/4">
                        {{ claim.coverage_description }}
                    </div>
                </div>
                <div class="flex w-full px-2 mb-2" data-cy="claim-is-bodily-injury-field" v-if="claim.is_motor">
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.is_bodily_injury') }}
                    </ui-label>
                    <div class="subText w-1/4 lg:w-2/4">
                        {{
                            claim.cb_is_bodily_injury
                                ? $t('claim.view.data.is_bodily_injury.yes')
                                : $t('claim.view.data.is_bodily_injury.no')
                        }}
                    </div>
                </div>
                <div
                    class="flex w-full px-2 mb-2"
                    data-cy="claim-file-source-field"
                    v-if="section_type == 'site_admin' || section_type == 'association'"
                >
                    <ui-label class="w-1/2 lg:w-1/5">
                        {{ $t('claim.view.labels.file_source') }}
                    </ui-label>
                    <div class="subText w-1/2 lg:w-3/4">
                        {{ claim.file_source }}
                    </div>
                </div>
            </div>
            <div
                :class="{
                    'section-header': section_type != 'site_admin' && section_type != 'association',
                    'p-4 pb-0': section_type == 'site_admin' || section_type == 'association',
                }"
            >
                <div class="view-borderline flex flex-wrap w-full mb-2">
                    <div
                        class="header-text w-full pt-3"
                        :class="{ 'pl-2': section_type != 'site_admin' && section_type != 'association' }"
                        data-cy="claim-third-party-header"
                    >
                        {{ $t('claim.view.headers.third_party') }}
                    </div>
                </div>
            </div>
            <div v-for="(claimant, index) in claim.third_party_claimants" :key="claimant.id" :data-key="claimant.id">
                <view-claimant data-cy="claimant-view" :claimant="claimant" />

                <div v-if="index != claim.third_party_claimants.length - 1" class="flex w-full border-b-2" />
            </div>
            <div class="section-body px-4 mb-4 w-full" v-if="claim.third_party_claimants.length == 0">
                <div class="block px-2 mb-6 text-center">
                    {{ $t('claim.view.data.third_party.no_data') }}
                </div>
            </div>
        </div>
        <div v-if="form_mode != 'drawer' && isCoverVaultEnabled" class="flex flex-row justify-end p-4">
            <a-button data-cy="check-cover-button" @click="showModal" type="primary">
                {{ $t('claim.view.buttons.cover') }}
            </a-button>
        </div>
        <div class="drawer-footer" v-if="form_mode == 'drawer'">
            <slot name="actions"></slot>

            <a-button data-cy="drawer-cancel-button" @click="onDrawerClose" class="btn-cancel">
                {{ $t('claim.view.buttons.cancel') }}
            </a-button>
        </div>
        <modal-covers :claim_id="claim_id" ref="CoversModal" />
    </div>
</template>

<script>
import UiLabel from '@/components/ui/Label.vue';
import ViewClaimant from '@/components/views/claimants/Belize.vue';
import ModalCovers from '@/components/modals/Covers.vue';

export default {
    components: {
        UiLabel,
        ViewClaimant,
        ModalCovers,
    },
    props: {
        claim: {
            type: Object,
            default: {
                third_party_claimants: [],
            },
        },
        section_type: {
            type: String,
            default: 'default',
        },
        claim_id: {
            type: String,
            default: '',
        },
        form_mode: {
            type: String,
            default: 'init',
        },
    },
    computed: {
        lastUpdatedAt() {
            return this.$t('claim.view.headers.last_updated_at', {
                updated_at: this.$dayjs(this.claim.updated_at).format('YYYY-MM-DD @ h:mm:ss A'),
            });
        },
        isCoverVaultEnabled() {
            return this.$store.state.user.modules.covervault;
        },
    },
    methods: {
        onDrawerClose() {
            this.$emit('onDrawerClose');
        },
        showModal() {
            this.$refs.CoversModal.showModal();
        },
    },
};
</script>
